'use client';

import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { QrCode, ChevronRight, Maximize2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useMyCheckinToken } from '@/lib/queries/my-dashboard';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog/dialog';

export interface EntrantCheckinQrCardProps {
  /** Override the default DGR-styled bar. */
  className?: string;
}

export function EntrantCheckinQrCard({ className }: EntrantCheckinQrCardProps) {
  const t = useTranslations('entrantDashboard.checkin');
  const { data, isLoading } = useMyCheckinToken();
  const [dataUri, setDataUri] = useState<string | null>(null);
  const [dataUriLarge, setDataUriLarge] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [enlargeOpen, setEnlargeOpen] = useState(false);

  useEffect(() => {
    if (!data?.token) {
      setDataUri(null);
      setDataUriLarge(null);
      return;
    }
    let cancelled = false;
    Promise.all([
      QRCode.toDataURL(data.token, { margin: 1, width: 320, errorCorrectionLevel: 'M' }),
      QRCode.toDataURL(data.token, { margin: 1, width: 720, errorCorrectionLevel: 'M' }),
    ])
      .then(([small, large]) => {
        if (cancelled) return;
        setDataUri(small);
        setDataUriLarge(large);
      })
      .catch(() => {
        if (cancelled) return;
        setDataUri(null);
        setDataUriLarge(null);
      });
    return () => {
      cancelled = true;
    };
  }, [data?.token]);

  if (isLoading || !data || !dataUri) {
    return null;
  }

  const containerClass =
    className ?? 'bg-stone-900 rounded-xl p-4 md:p-6 border border-stone-800 w-full';

  return (
    <>
      {/* Desktop: inline QR beside label. Hidden on mobile. */}
      <div className={`${containerClass} hidden md:block`}>
        <div className="flex items-start gap-3">
          <QrCode className="w-5 h-5 text-dgr-gold mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-stone-400 text-sm">{t('title')}</p>
            <p className="text-white font-medium text-base">{t('instruction')}</p>
            <p className="text-stone-500 text-xs mt-1">{data.eventName}</p>
            <button
              type="button"
              onClick={() => setEnlargeOpen(true)}
              className="mt-2 inline-flex items-center gap-1.5 text-dgr-gold text-sm font-medium hover:underline focus:outline-none focus:underline"
            >
              <Maximize2 className="w-3.5 h-3.5" />
              Enlarge
            </button>
          </div>
          <div className="flex-shrink-0 rounded-lg bg-white p-2">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={dataUri}
              alt="Event check-in QR code"
              width={140}
              height={140}
              className="block h-32 w-32 lg:h-36 lg:w-36"
            />
          </div>
        </div>
      </div>

      {/* Desktop enlarge dialog */}
      <Dialog open={enlargeOpen} onOpenChange={setEnlargeOpen}>
        <DialogContent className="sm:max-w-xl bg-stone-900 border-stone-800 text-white">
          <DialogHeader className="text-left">
            <DialogTitle className="text-white flex items-center gap-2">
              <QrCode className="w-5 h-5 text-dgr-gold" />
              {t('title')}
            </DialogTitle>
            <DialogDescription className="text-stone-400">{t('instruction')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3 py-2">
            <div className="rounded-lg bg-white p-4">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={dataUriLarge ?? dataUri}
                alt="Event check-in QR code"
                width={480}
                height={480}
                className="block h-[min(70vh,480px)] w-[min(70vh,480px)]"
              />
            </div>
            <p className="text-sm text-stone-400">{data.eventName}</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mobile: compact bar + sheet trigger. Hidden on desktop. */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            className={`${containerClass} md:hidden text-left transition-colors hover:bg-stone-800/60 focus:outline-none focus:ring-2 focus:ring-dgr-gold/50`}
          >
            <div className="flex items-center gap-2">
              <QrCode className="w-4 h-4 text-dgr-gold mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-stone-400 text-xs">{t('title')}</p>
                <p className="text-white font-medium text-sm truncate">Show Check-In QR Code</p>
              </div>
              <ChevronRight className="w-4 h-4 text-dgr-gold flex-shrink-0" />
            </div>
          </button>
        </SheetTrigger>
        <SheetContent
          side="bottom"
          className="bg-stone-900 border-stone-800 text-white rounded-t-2xl"
        >
          <SheetHeader className="text-left">
            <SheetTitle className="text-white flex items-center gap-2">
              <QrCode className="w-5 h-5 text-dgr-gold" />
              {t('title')}
            </SheetTitle>
            <SheetDescription className="text-stone-400">{t('instruction')}</SheetDescription>
          </SheetHeader>
          <div className="mt-4 flex flex-col items-center gap-3">
            <div className="rounded-lg bg-white p-3">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={dataUri}
                alt="Event check-in QR code"
                width={280}
                height={280}
                className="block h-64 w-64"
              />
            </div>
            <p className="text-xs text-stone-500">{data.eventName}</p>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
